import { FC, useEffect, useMemo, useRef, useState } from "react";
import type { Candlestick, Market } from "@module-bindings/types";
import { assetIdKey, getAssetDecimals } from "@/lib/marketUtils";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { cn } from "@/lib/utils";

const FRAC_BITS = 60n;

/**
 * A smooth transactions-per-second rate derived from the monotonic
 * `total_events` counter, rather than the server's per-second bucket (which
 * flickers to 0 on any sub-second-rate gap). Samples the counter on a 1s timer
 * and reports the average rate over the last `windowMs`, so it rises with
 * activity and decays to 0 only when genuinely idle.
 */
function useRollingTps(totalEvents: bigint, windowMs = 5000): number {
  const [rate, setRate] = useState(0);
  const latest = useRef(totalEvents);
  latest.current = totalEvents;
  const samples = useRef<{ t: number; n: bigint }[]>([]);
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      const s = samples.current;
      s.push({ t: now, n: latest.current });
      while (s.length > 1 && s[0].t < now - windowMs) s.shift();
      const first = s[0];
      const last = s[s.length - 1];
      const dt = (last.t - first.t) / 1000;
      const dn = Number(last.n - first.n);
      setRate(dt > 0 ? Math.max(0, dn / dt) : 0);
    }, 1000);
    return () => clearInterval(id);
  }, [windowMs]);
  return rate;
}

function priceToNumber(raw: bigint): number {
  const integer = Number(raw >> FRAC_BITS);
  const fractional = Number(raw & ((1n << FRAC_BITS) - 1n)) / Number(1n << FRAC_BITS);
  return integer + fractional;
}

interface MarketStatsProps {
  selectedMarket: Market | null;
  className?: string,
  itemClassName?: string,
  columnsClassName?: string,
}

interface MarketStat {
  label: string;
  value: string;
  placeholder?: boolean;
  sub?: string;
}

export const useMarketStats = (selectedMarket: Market | null): MarketStat[] => {
  const { ledgerView } = useGummiwormContext();
  const pairKey = selectedMarket
    ? `${assetIdKey(selectedMarket.tradingPair.base)}/${assetIdKey(selectedMarket.tradingPair.quote)}`
    : null;

  const tpsStats = ledgerView?.tpsStats;
  const candles = useMemo(
    () => pairKey != null
      ? Object.values(ledgerView?.candlesticks ?? {}).filter(c => c.pairKey === pairKey && c.granularity !== "1m") as Candlestick[]
      : [],
    [ledgerView?.candlesticks, pairKey],
  );
  
  const totalEvents = tpsStats?.totalEvents ?? 0n;
  const tps = useRollingTps(totalEvents);

  // Get hourly candle for volume
  // and daily candle for 24h stats
  const { hourlyCandle, dailyCandle } = useMemo(() => {
    if (!pairKey) return { hourlyCandle: null, dailyCandle: null };
    let hourlyCandle: Candlestick | null = null;
    let dailyCandle: Candlestick | null = null;
    for (const candle of candles) {
      if (candle.granularity === "1h" && candle.timestamp > (hourlyCandle?.timestamp ?? 0n)) {
        hourlyCandle = candle;
      }
      if (candle.granularity === "1d" && candle.timestamp > (dailyCandle?.timestamp ?? 0n)) {
        dailyCandle = candle;
      }
    }
    return { hourlyCandle, dailyCandle };
  }, [candles, pairKey]);


  if (!selectedMarket) return [];

  const baseDecimals = selectedMarket ? getAssetDecimals(selectedMarket.tradingPair.base) : 6;
  const hourlyVolume = hourlyCandle ? Number(hourlyCandle.volume) / 10 ** baseDecimals : 0;
  const hourlyTrades = hourlyCandle ? hourlyCandle.tradeCount : 0;

  const dayHigh = dailyCandle ? priceToNumber(dailyCandle.high) : null;
  const dayLow = dailyCandle ? priceToNumber(dailyCandle.low) : null;

  return [
    {
      label: "Volume (1h)",
      value: hourlyVolume > 0 ? formatVolume(hourlyVolume) : "—",
      placeholder: hourlyVolume === 0,
    },
    {
      label: "24h High",
      value: dayHigh != null ? dayHigh.toFixed(6) : "—",
      placeholder: dayHigh == null,
    },
    {
      label: "24h Low",
      value: dayLow != null ? dayLow.toFixed(6) : "—",
      placeholder: dayLow == null,
    },
    {
      label: "Trades (1h)",
      value: hourlyTrades > 0 ? hourlyTrades.toLocaleString() : "—",
      placeholder: hourlyTrades === 0,
    },
    {
      label: "TPS",
      value: tps.toFixed(1),
    },
    {
      label: "Total Txs",
      value: totalEvents.toLocaleString(),
    },
  ] satisfies MarketStat[];
}

export const MarketStats: FC<MarketStatsProps> = ({
  selectedMarket,
  className,
  itemClassName,
  columnsClassName,
}) => {
  const stats = useMarketStats(selectedMarket);

  return (
    <div className={cn("grid h-fit grid-cols-2 gap-1 lg:grid-cols-6", columnsClassName, className)}>
      {stats.map((stat) => (
        <div
          key={stat.label}
          className={cn("sr-panel flex min-h-[92px] flex-1 items-start px-5 py-4", itemClassName)}
        >
          <div className="flex h-fit flex-col">
            <span className="mb-1.5 block text-[11px] font-medium text-sr-neutral-300">
              {stat.label}
            </span>
            <span className={`block text-base font-medium font-display ${stat.value === "—" ? "text-sr-neutral-700" : "text-white"}`}>
              {stat.value}
            </span>
            {stat.sub && (
              <span className="mt-0.5 block text-[11px] text-sr-neutral-300">
                {stat.sub}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

function formatVolume(v: number): string {
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(2)}B`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
}
